import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BarcodeFormat, BarcodeScanner } from "@capacitor-mlkit/barcode-scanning";
import { toast } from "sonner";
import { App } from "@/lib/capacitor/app";
import { logger } from "@/logger";

interface UseNativeQRScannerOptions {
  onScanSuccess: (data: string) => void | Promise<void>;
  onError?: (error: Error) => void;
}

export function useNativeQRScanner({ onScanSuccess, onError }: UseNativeQRScannerOptions) {
  const { t } = useTranslation();
  const [isScanning, setIsScanning] = useState(false);
  const lastScanRef = useRef<string>();

  const checkPermissions = useCallback(async () => {
    try {
      const { camera } = await BarcodeScanner.checkPermissions();
      if (camera === "granted" || camera === "limited") {
        return true;
      }

      const { camera: newPermission } = await BarcodeScanner.requestPermissions();
      return newPermission === "granted" || newPermission === "limited";
    } catch (error) {
      logger.error("Failed to check camera permissions", error);
      return false;
    }
  }, []);

  const scanQRCode = useCallback(async () => {
    // Prevent multiple concurrent scans
    if (isScanning) {
      return;
    }

    // Check permissions first
    const hasPermission = await checkPermissions();
    if (!hasPermission) {
      // Dezelfde reden als bij een profielfoto en een bijlage: geweigerd wordt niet opnieuw
      // gevraagd, dus zonder deze knop is de melding een doodlopende weg.
      toast.error(t("qr-scanner:errors.camera-permission"), {
        action: {
          label: t("common:open-settings"),
          onClick: () => void App.openSettings(),
        },
      });
      return;
    }

    try {
      setIsScanning(true);

      // Add scanner-active class to hide page content
      document.body.classList.add("scanner-active");

      // Open native scanner directly
      const result = await BarcodeScanner.scan({
        formats: [BarcodeFormat.QrCode],
      });

      // Check if scan was successful
      if (result.barcodes.length > 0 && result.barcodes[0].rawValue) {
        const scannedData = result.barcodes[0].rawValue;

        // Prevent duplicate processing of the same code
        if (lastScanRef.current === scannedData) {
          return;
        }

        lastScanRef.current = scannedData;

        // Call the success handler
        await onScanSuccess(scannedData);

        // Clear last scan after a delay to allow re-scanning the same code later
        setTimeout(() => {
          lastScanRef.current = undefined;
        }, 2000);
      }
    } catch (error) {
      logger.error("QR scanning failed", error);

      // Only show error if user didn't cancel
      if (error instanceof Error && !error.message.includes("cancelled")) {
        toast.error(t("qr-scanner:errors.scan-failed"));
        onError?.(error);
      }
    } finally {
      // Always remove the scanner-active class
      document.body.classList.remove("scanner-active");
      setIsScanning(false);
    }
  }, [checkPermissions, isScanning, onScanSuccess, onError, t]);

  // Cleanup effect to ensure scanner-active class is removed
  useEffect(() => {
    return () => {
      document.body.classList.remove("scanner-active");
    };
  }, []);

  return {
    scanQRCode,
    isScanning,
  };
}
